// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import { useUniqueId } from '@tectonics/hooks/use-unique-id';
import { useUserInputValue, useUserInputAsCheckbox } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError } from '@tectonics/prs/validation/use-user-input-validation-result';
import { type UserInputValidationError } from '@tectonics/prs/validation/types';
import { useSubmitting } from '@tectonics/prs/submitting';
import { detectCardBrand } from '@tectonics/prs/card-brand';
import { Checkbox, Link } from '../../../../../atoms';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { ErrorLabel } from '../../../../../molecules/error-label';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';

type Props = {|
  brand?: string,
|};

export function FieldAcceptVisaTerms({ brand }: Props): Node {
  const [cardNumber] = useUserInputValue('cardNumber');
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('acceptVisaTerms');

  let cardBrand = brand;

  if (ShouldDetermineCardBrand(cardBrand, cardNumber)) {
    cardBrand = detectCardBrand(cardNumber);
  }

  if (cardBrand === 'visa') {
    return (
      <Root datatest-id={'visaTermsCheckbox'}>
        <Message />
        <AcceptCheckbox />

        {formHasBeenSent && validationError && (
          <ValidationMessage validationError={validationError} />
        )}
      </Root>
    );
  }

  return null;
}

const Root = styled.div`
  display: grid;
  grid-gap: 12px;
`;

function Message(): Node {
  return (
    <MessageRoot>
      Seu pagamento está sendo efetuado através de uma operação de câmbio, processado pelo EBANX.
    </MessageRoot>
  );
}

const MessageRoot = styled.p``;

function AcceptCheckbox(): Node {
  const inputId = useUniqueId();
  const [acceptVisaTerms, handleChangeAcceptVisaTerms] = useUserInputAsCheckbox('acceptVisaTerms');

  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('acceptVisaTerms');
  const submitting = useSubmitting();

  return (
    <AcceptCheckboxRoot>

      <Checkbox
        id={inputId}
        checked={acceptVisaTerms}
        onChange={handleChangeAcceptVisaTerms}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="accept-visa-terms"
      />

      <AcceptCheckboxLabel htmlFor={inputId} />

    </AcceptCheckboxRoot>
  );
}

const AcceptCheckboxRoot = styled.div``;

function AcceptCheckboxLabel(props): Node {
  return (
    <AcceptCheckboxLabelRoot {...props}>
      Aceito os <Link href="https://www.ebanx.com/br/termos">Termos e Condições</Link>
    </AcceptCheckboxLabelRoot>
  );
}

const AcceptCheckboxLabelRoot = styled.label`
  margin-left: 1em;
`;

type ValidationMessageProps = {
  validationError: UserInputValidationError,
};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  switch (validationError.id) {
    case 'agreement':
      return <ErrorLabel>Para concluir esse pagamento, é preciso aceitar os Termos e Condições</ErrorLabel>;

    default:
      return <ErrorLabel>{validationError.id}</ErrorLabel>;
  }
}

function ShouldDetermineCardBrand(cardBrand: ?string, cardNumber): boolean{
  const incomingPayment = useIncomingPayment();
  const [selectedPaymentMethod] = useUserInputValue('selectedPaymentMethod');
  const country = getCountry(incomingPayment);

  return selectedPaymentMethod === 'creditcard' && country === 'br' && cardNumber;
}
